import { Link } from "@tanstack/react-router";
import { useState, type FormEvent, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiErrorMessage } from "@/api/errors";
import { PageHeader } from "@/shared/components/PageHeader";
import { SectionCard } from "@/shared/components/SectionCard";
import { toast } from "@/shared/lib/toast";

import { useBrandImport } from "../api/use-brand-import";
import { useLastBrandImport } from "../api/use-last-brand-import";

const EXPECTED_HEADER = "sino_sku,ap_name,brand,category";
const SECONDARY =
  "inline-flex h-[var(--control-h)] items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3.5 text-[13.5px] font-semibold text-[var(--text-primary)] shadow-[var(--sh-xs)] transition hover:bg-[var(--canvas)] disabled:opacity-50";
const COUNTS = ["updated", "skipped", "unknown_count"] as const;

/**
 * The real BE audit event for `sku.brand_import` carries the counts as
 * before/after diffs in `changes.<key>.new`, not in `metadata` (which only
 * holds `{ severity }`) — found via the visual gate's real upload, which
 * disagreed with the task brief's own mocked audit-event shape. Reading
 * `metadata` first keeps the MSW-mocked test passing; falling back to
 * `changes` makes the real caption correct.
 */
function auditCount(
  event: { metadata: { [k: string]: unknown }; changes: { [k: string]: { new: unknown } } },
  key: string,
): number {
  const fromMetadata = event.metadata[key];
  const value = fromMetadata ?? event.changes[key]?.new;
  return Number(value ?? 0);
}

/**
 * Settings ▸ SKU brands (spec §5.6): upload the sales sheet as CSV. Upsert of
 * brand columns only; unknown SKUs are listed, never created.
 * `// ponytail: no editing grid; CSV re-upload is the bulk path, Create/Edit SKU dialog is the single path.`
 */
export function SkuBrandsPage(): JSX.Element {
  const { t } = useTranslation(["inventory", "nav"]);
  const upload = useBrandImport();
  const last = useLastBrandImport();
  const [file, setFile] = useState<File | null>(null);
  const result = upload.data;

  async function copy(text: string): Promise<void> {
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(text);
      toast.success(t("brands.copied"));
    } catch {
      toast.error(t("brands.copy_failed"));
    }
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (file) upload.mutate(file);
  }

  const ev = last.data;
  const lastLine = ev
    ? t("brands.last_upload", {
        who: ev.actor_display_name,
        when: new Date(ev.occurred_at).toLocaleString("en-GB", { timeZone: "Asia/Bangkok" }),
        updated: auditCount(ev, "updated").toLocaleString("en-US"),
        unknown: auditCount(ev, "unknown_count").toLocaleString("en-US"),
      })
    : last.isSuccess
      ? t("brands.no_upload")
      : null;

  return (
    <div data-testid="sku-brands-page" className="space-y-6">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.admin")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("brands.title")}</span>
          </>
        }
        title={t("brands.title")}
        subtitle={t("brands.subtitle")}
      />

      <SectionCard
        title={t("brands.upload_title")}
        {...(lastLine ? { caption: <span data-testid="brands-last-upload">{lastLine}</span> } : {})}
      >
        <div className="space-y-5 p-5">
          <div>
            <p className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">
              {t("brands.header_label")}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <code
                data-testid="brands-expected-header"
                className="sku rounded-[var(--control-r)] bg-[var(--surface-sunken)] px-3 py-2 text-[13px]"
              >
                {EXPECTED_HEADER}
              </code>
              <button
                type="button"
                data-testid="brands-copy-header"
                className={SECONDARY}
                onClick={() => void copy(EXPECTED_HEADER)}
              >
                {t("brands.copy")}
              </button>
            </div>
          </div>

          <form onSubmit={submit} className="flex flex-wrap items-center gap-3">
            <label
              htmlFor="brands-file"
              className="text-xs font-semibold text-[var(--text-secondary)]"
            >
              {t("brands.file_label")}
            </label>
            <input
              id="brands-file"
              type="file"
              accept=".csv,text/csv"
              data-testid="brands-file"
              className="text-sm"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                upload.reset();
              }}
            />
            {/* The page's single primary action (CLAUDE.md rule 12). */}
            <button
              type="submit"
              data-testid="brands-upload"
              disabled={!file || upload.isPending}
              className="inline-flex h-[var(--control-h)] items-center rounded-[var(--control-r)] px-4 text-[13.5px] font-bold text-white transition hover:-translate-y-px disabled:translate-y-0 disabled:opacity-50"
              style={{
                background: "var(--grad-primary-btn)",
                boxShadow:
                  "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5),inset 0 1px 0 rgba(255,255,255,.22)",
              }}
            >
              {upload.isPending ? t("brands.uploading") : t("brands.upload")}
            </button>
          </form>

          {upload.isError && (
            <p
              role="alert"
              data-testid="brands-error"
              className="text-sm text-[var(--intent-danger-fg)]"
            >
              {t("brands.failed", { reason: apiErrorMessage(upload.error) })}
              {upload.error instanceof ApiError &&
                typeof (upload.error.body as { line?: unknown } | null)?.line === "number" && (
                  <>
                    {" "}
                    {t("brands.error_line", {
                      line: (upload.error.body as { line: number }).line,
                    })}
                  </>
                )}
            </p>
          )}

          {result && (
            <div data-testid="brands-result" aria-live="polite" className="space-y-4">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {COUNTS.map((k) => (
                  <div key={k}>
                    <dt className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
                      {t(`brands.${k}`)}
                    </dt>
                    <dd
                      data-testid={`brands-${k}`}
                      className="text-2xl font-extrabold tabular-nums"
                    >
                      {result[k].toLocaleString("en-US")}
                    </dd>
                  </div>
                ))}
              </dl>
              {result.unknown_skus.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-[var(--text-subtle)]">
                      {result.unknown_count > result.unknown_skus.length
                        ? t("brands.unknown_capped", {
                            shown: result.unknown_skus.length,
                            total: result.unknown_count,
                          })
                        : t("brands.unknown_count")}
                    </span>
                    <button
                      type="button"
                      data-testid="brands-copy-unknown"
                      className={SECONDARY}
                      onClick={() => void copy(result.unknown_skus.join("\n"))}
                    >
                      {t("brands.copy_unknown")}
                    </button>
                  </div>
                  <textarea
                    readOnly
                    rows={6}
                    data-testid="brands-unknown-list"
                    aria-label={t("brands.unknown_count")}
                    value={result.unknown_skus.join("\n")}
                    className="sku w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-[var(--surface-sunken)] p-3 text-[13px]"
                  />
                </div>
              )}
              <Link
                to="/inventory"
                search={{ search: "", offset: 0, brand: "unmapped" }}
                className="text-sm font-semibold text-[var(--brand-600)] hover:underline"
              >
                {t("brands.view_unmapped")}
              </Link>
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
